import {EpisodeOfCareSummaryChartMetricType, EpisodeOfCareSummaryChartType, EpisodeOfCareSummaryLaterality, EpisodeOfCareSummaryType} from "@/constants/enums.ts";
import type {AxiosResponse} from 'axios';
import type {ApiResponse} from '@/types/apiTypes';
import axios from '@/api/axiosConfig';
import {ApiRoutes} from '@/api/ApiRoutes';
import type {PatientDailyStatisticType, PatientDataSourcesResponse, PatientProgressResponse, PatientStatisticsResponse} from 'src/types/webContracts';

// START - Get EOC AI Summaries
export interface EocSummaries {
  pageCount: number;
  summaries: EocSummariesItem[];
}

export interface EocSummariesGroupedByMonth {
  month: string;
  summaries: EocSummariesItem[];
}

export interface EocSummariesItem {
  deliveryDate: string;
  id: number;
  summaryType: EpisodeOfCareSummaryType;
  title: string;
  viewedDate: string | null;
}

export async function getEocSummaries(eocId: number, pgeNum: number | null = null, pgeSize: number | null = null): Promise<AxiosResponse<ApiResponse<EocSummaries>>> {
  return await axios.get(ApiRoutes.Statistics.GetEocSummaries, { params: { eocId, pgeNum, pgeSize } })
}
// END - Get EOC AI Summaries

// START - Get EOC AI Summary Details
export interface EocSummaryChartData {
  chartType: EpisodeOfCareSummaryChartType;
  dataPoints: EocSummaryChartDataPoint[];
  laterality: EpisodeOfCareSummaryLaterality | null;
  metricLocalized: string;
  metricType: EpisodeOfCareSummaryChartMetricType | null;
}

export interface EocSummaryChartDataPoint {
  date: string | null;
  label: string;
  value: number | null;
}

export interface EocSummaryDetails {
  charts: EocSummaryChartData[];
  deliveryDate: string;
  details: string;
  id: number;
  summaryType: EpisodeOfCareSummaryType;
  title: string;
  viewedDate: string | null;
}

export async function getEocSummaryDetails(eocId: number, sumId: number): Promise<AxiosResponse<ApiResponse<EocSummaryDetails>>> {
  return await axios.get(ApiRoutes.Statistics.GetEocSummaryDetails, { params: { eocId, sumId } });
}
// END - Get EOC AI Summary Details

export async function getPatientDataSources(): Promise<AxiosResponse<ApiResponse<PatientDataSourcesResponse>>> {
  return await axios.get(ApiRoutes.Statistics.PatientDataSources);
}

export async function getPatientMeasurementStats(startDate: string, endDate: string, measurementType: string, source: string, device: number): Promise<AxiosResponse<ApiResponse<Record<string, PatientDailyStatisticType>>>> {
  return await axios.get(ApiRoutes.Statistics.PatientMeasurementStats, { params: { startDate, endDate, measurementDataTypeNames: measurementType, source, device } });
}

export async function getPatientProgressComparison(eocId: number, metric: string, eocDeviceId?: number): Promise<AxiosResponse<ApiResponse<PatientProgressResponse>>> {
  return await axios.get(ApiRoutes.Statistics.PatientProgress, { params: { eocId, metric, eocDeviceId } });
}

export async function getPatientStatisticSummaries(source?: string, device?: number): Promise<AxiosResponse<ApiResponse<PatientStatisticsResponse>>> {
  return await axios.get(ApiRoutes.Statistics.PatientStatisticSummaries,  { params: { source, device } });
}
